import { createSelectorMemoized } from '@base-ui/utils/store';
import type {
  RecurringEventPresetKey,
  SchedulerProcessedEventRecurrenceRule,
  SchedulerProcessedDate,
} from '../models';
import type { SchedulerState as State } from '../internals/utils/SchedulerStore/SchedulerStore.types';

const selectRecurringEventsPlugin = (state: State) => state.recurringEventsPlugin;

/** Memoized selectors that delegate to the recurring-events plugin. */
export const schedulerRecurringEventSelectors = {
  presets: createSelectorMemoized(
    (state: State) => state.adapter,
    selectRecurringEventsPlugin,
    (
      adapter,
      recurringEventsPlugin,
      date: SchedulerProcessedDate,
    ): Record<RecurringEventPresetKey, SchedulerProcessedEventRecurrenceRule> | null =>
      recurringEventsPlugin?.computePresets(adapter, date) ?? null,
  ),
  defaultPresetKey: createSelectorMemoized(
    (state: State) => state.adapter,
    selectRecurringEventsPlugin,
    (
      adapter,
      recurringEventsPlugin,
      rule: SchedulerProcessedEventRecurrenceRule | undefined,
      occurrenceStart: SchedulerProcessedDate,
    ): RecurringEventPresetKey | 'custom' | null =>
      recurringEventsPlugin?.getDefaultPresetKey(adapter, rule, occurrenceStart) ?? null,
  ),
  isSameRRule: (
    state: State,
    rruleA: SchedulerProcessedEventRecurrenceRule | undefined,
    rruleB: SchedulerProcessedEventRecurrenceRule | undefined,
  ): boolean => {
    if (!rruleA && !rruleB) {
      return true;
    }
    if (!rruleA || !rruleB) {
      return false;
    }
    return selectRecurringEventsPlugin(state)?.isSameRRule(state.adapter, rruleA, rruleB) ?? false;
  },
};
